import { z } from 'zod';

/**
 * A run's transcript, as agent-harness writes it: one `<step>-<attempt>.jsonl` per session plus `summary.json`.
 * It holds script text, so it lives in the staff-only `run-logs` bucket at `<project_id>/<run_id>/<file>` and
 * never reaches a log line, Sentry, an audit row or an error message.
 */
export const RUN_LOG_BUCKET = 'run-logs';
export const RUN_LOG_SUMMARY_FILE = 'summary.json';

export const RUN_LOG_RECORD_KINDS = ['message', 'stderr', 'check', 'harness'] as const;

/** One JSONL line: `data` is narrowed per kind with the schemas below when it is shown. */
export const runLogRecordSchema = z.object({
  ts: z.string(),
  step: z.string().min(1),
  attempt: z.number().int().positive(),
  kind: z.enum(RUN_LOG_RECORD_KINDS),
  data: z.unknown(),
});
export type RunLogRecord = z.infer<typeof runLogRecordSchema>;

const textBlock = z.looseObject({ type: z.literal('text'), text: z.string() });
const thinkingBlock = z.looseObject({ type: z.literal('thinking'), thinking: z.string() });
const toolUseBlock = z.looseObject({
  type: z.literal('tool_use'),
  id: z.string(),
  name: z.string(),
  input: z.unknown(),
});
const toolResultBlock = z.looseObject({
  type: z.literal('tool_result'),
  tool_use_id: z.string(),
  content: z
    .union([z.string(), z.array(z.looseObject({ type: z.string(), text: z.string().optional() }))])
    .optional(),
  is_error: z.boolean().optional(),
});
/** Any other block (an image, say) is kept as its type only. */
const otherBlock = z
  .looseObject({ type: z.string() })
  .transform((block) => ({ type: 'other' as const, blockType: block.type }));
const contentBlock = z.union([textBlock, thinkingBlock, toolUseBlock, toolResultBlock, otherBlock]);
export type RunLogContentBlock = z.infer<typeof contentBlock>;

/** The `data` of a `message` record: an SDK message. A type not listed here does not parse and is shown raw. */
export const runLogMessageSchema = z.discriminatedUnion('type', [
  z.looseObject({
    type: z.literal('assistant'),
    message: z.looseObject({ content: z.array(contentBlock) }),
  }),
  z.looseObject({
    type: z.literal('user'),
    message: z.looseObject({ content: z.union([z.string(), z.array(contentBlock)]) }),
  }),
  z.looseObject({ type: z.literal('system'), subtype: z.string().optional() }),
  z.looseObject({ type: z.literal('result'), subtype: z.string().optional() }),
]);
export type RunLogMessage = z.infer<typeof runLogMessageSchema>;

/** A check's outcome, in a `check` record. */
export const runLogCheckResultSchema = z.looseObject({
  id: z.string(),
  passed: z.boolean(),
  condition: z.string(),
  message: z.string().optional(),
  excepted: z.object({ reason: z.string() }).optional(),
});
export type RunLogCheckResult = z.infer<typeof runLogCheckResultSchema>;

/** The raw output of the exit condition that ran the checks, in a `check` record. */
export const runLogConditionOutputSchema = z.looseObject({
  condition: z.string(),
  stdout: z.string(),
  stderr: z.string(),
  exitCode: z.number().int().optional(),
});
export type RunLogConditionOutput = z.infer<typeof runLogConditionOutputSchema>;

/** The `data` of a `check` record: a check's outcome, or the raw output of the exit condition that ran it. */
export const runLogCheckSchema = z.union([runLogCheckResultSchema, runLogConditionOutputSchema]);
export type RunLogCheck = z.infer<typeof runLogCheckSchema>;

/** The `data` of a `harness` record: what agent-harness itself did around a session. */
export const runLogHarnessSchema = z.discriminatedUnion('event', [
  z.looseObject({
    event: z.literal('start'),
    model: z.string(),
    effort: z.string(),
    skills: z.array(z.string()),
    prompt: z.string().optional(),
    resume: z.string().optional(),
  }),
  z.looseObject({
    event: z.literal('result'),
    subtype: z.string(),
    isError: z.boolean(),
    numTurns: z.number(),
    costUsd: z.number(),
    durationMs: z.number(),
    finalText: z.string().optional(),
  }),
  z.looseObject({ event: z.literal('error'), message: z.string() }),
  z.looseObject({
    event: z.literal('retry'),
    next: z.number(),
    max: z.number(),
    reason: z.string(),
  }),
  z.looseObject({ event: z.literal('warning'), message: z.string() }),
]);
export type RunLogHarness = z.infer<typeof runLogHarnessSchema>;

/** The parts of agent-harness's `RunSummary` the log page shows; the rest passes through. */
export const runLogSummarySchema = z.looseObject({
  status: z.enum(['passed', 'failed', 'aborted', 'error']),
  workflow: z.string(),
  startedAt: z.string(),
  finishedAt: z.string(),
  totalCostUsd: z.number(),
  totalDurationMs: z.number(),
  steps: z.array(
    z.looseObject({
      name: z.string(),
      status: z.string(),
      attempts: z.array(
        z.looseObject({
          kind: z.string(),
          numTurns: z.number().optional(),
          costUsd: z.number().optional(),
          durationMs: z.number().optional(),
          error: z.string().optional(),
        }),
      ),
    }),
  ),
  failure: z.looseObject({ step: z.string(), reason: z.string() }).optional(),
});
export type RunLogSummary = z.infer<typeof runLogSummarySchema>;

/** `<project_id>/<run_id>/<file>` in the `run-logs` bucket. */
export function runLogObjectName(projectId: string, runId: string, file: string): string {
  return `${projectId}/${runId}/${file}`;
}

/** What a run log object holds, from its name: one step attempt, the summary, or neither (null). */
export function parseRunLogObject(
  name: string,
): { step: string; attempt: number } | 'summary' | null {
  const file = name.slice(name.lastIndexOf('/') + 1);
  if (file === RUN_LOG_SUMMARY_FILE) return 'summary';
  const match = /^(.+)-([1-9][0-9]*)\.jsonl$/.exec(file);
  if (!match?.[1] || !match[2]) return null;
  return { step: match[1], attempt: Number(match[2]) };
}

/** What `staff_run_log_open` answers: the run's project and its log objects, by name. */
export const staffRunLogOpenSchema = z.object({
  project_id: z.uuid(),
  objects: z.array(z.object({ name: z.string().min(1), size: z.number().int().nonnegative() })),
});
export type StaffRunLogOpen = z.infer<typeof staffRunLogOpenSchema>;
